import type { ReadTransaction, WriteTransaction } from 'replicache'
import type {
  Commit,
  Integration,
  READMEAdditionalInfo,
  READMEScreenshots,
  VercelDeployButtonConfig,
} from '@basehub/data/replicache/index'
import { teamMutators } from '../team/mutators'
import {
  integrationBaseOps,
  Repository,
  Invite,
  repositoryBaseOps,
  teamBaseOps,
} from '@basehub/data/replicache/index'
import { teamSpaceMutators } from '../mutators'
import { invariant } from '@epic-web/invariant'
import { getIdempotentId } from '@basehub/utils/id/index'
import { hasAccessOrThrow, isUserIdOwner, isUserRole } from '../team/auth'
import { WithServerSecret } from '../../../tx/spec'
import { serverAuthorizedOrThrow } from '../../../tx/server-auth'
import { checkInviteValidity } from '../invite-util'
import { ensureBaseHubTransaction } from '@basehub/data/replicache/tx/iso'
import { _getRepoSlugForTitle } from './util'

async function createRepository(
  tx: WriteTransaction,
  {
    teamId,
    userId,
    repository,
  }: WithServerSecret<{
    teamId: string
    userId: string
    repository: Pick<
      Repository,
      'id' | 'title' | 'createdAt' | 'tokens' | 'isPublic'
    >
  }>
) {
  ensureBaseHubTransaction(tx)
  const [slug, team] = await Promise.all([
    getRepoSlugForTitle(tx, {
      title: repository.title,
      id: repository.id,
    }),
    teamBaseOps.get(tx, teamId),
  ])

  invariant(team, 'team was not found')
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  const isPublic = repository.isPublic ?? false
  const newRepo: Repository = {
    ...repository,
    isPublic,
    status: 'active',
    // TODO: Select this when creating a new repo
    slug,
    contributors: [userId],
    latestCommit: null,
    branching: { archiveAfterMerging: false },
  }
  await Promise.all([
    // create it
    repositoryBaseOps.put(tx, newRepo),
    // add it to team
    teamBaseOps.update(tx, {
      id: teamId,
      repoIds: [...team.repoIds, repository.id],
    }),
    // create event
    teamSpaceMutators.createEvent(tx, {
      event: {
        id: getIdempotentId({ idSeed: repository.id }),
        type: 'repo-create',
        teamId,
        userId,
        createdAt: repository.createdAt,
        repoId: repository.id,
        repoName: repository.title,
      },
    }),
  ])
  return newRepo
}

async function getRepoSlugForTitle(
  tx: WriteTransaction,
  { title, id }: Pick<Repository, 'title' | 'id'>
) {
  const repositories = await repositoryBaseOps.list(tx)

  return _getRepoSlugForTitle({ title, id, repos: repositories })
}

async function getRepoBySlug(tx: ReadTransaction, slug: string) {
  const repositories = await repositoryBaseOps.list(tx)

  return repositories.find((repo) => repo.slug === slug)
}

async function updateLatestCommit(
  tx: WriteTransaction,
  { repoId, latestCommit }: { repoId: string; latestCommit: Commit }
) {
  const repository = await repositoryBaseOps.get(tx, repoId)

  if (!repository) {
    throw new Error(`Repo ${repoId} not found`)
  }

  await repositoryBaseOps.update(tx, { id: repoId, latestCommit })
  return repository
}

async function addContributor(
  tx: WriteTransaction,
  { repoId, userId }: { repoId: string; userId: string }
) {
  const repository = await repositoryBaseOps.get(tx, repoId)

  if (!repository) {
    throw new Error(`Repo ${repoId} not found`)
  }

  await repositoryBaseOps.update(tx, {
    id: repoId,
    contributors: [
      // first remove the user if they already exist
      ...repository.contributors.filter((c) => c !== userId),
      userId,
    ],
  })
}

async function deleteRepository(
  tx: WriteTransaction,
  {
    repoId,
    teamId,
    userId,
    eventId,
    isoNow,
  }: WithServerSecret<{
    repoId: string
    teamId: string
    userId: string
    eventId: string
    isoNow: string
  }>
) {
  ensureBaseHubTransaction(tx)
  const [team, repo] = await Promise.all([
    teamMutators.getTeam(tx, teamId),
    teamSpaceMutators.getRepository(tx, repoId),
  ])
  if (!team) throw new Error(`team ${teamId} not found`)
  if (!repo) throw new Error(`repo ${repoId} not found`)

  hasAccessOrThrow({
    serverSecret: tx.serverSecret,
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  await Promise.all([
    repositoryBaseOps.delete(tx, repoId),
    teamMutators.removeRepoFromTeam(tx, { teamId, repoId }),
    isoNow &&
      eventId &&
      userId &&
      teamSpaceMutators.createEvent(tx, {
        event: {
          type: 'repo-delete',
          teamId: team.id,
          repoId: repoId,
          createdAt: isoNow,
          id: eventId,
          repoName: repo.title,
          userId: userId,
        },
      }),
  ])

  return repo
}

async function renameRepository(
  tx: WriteTransaction,
  {
    repoId,
    teamId,
    userId,
    eventId,
    isoNow,
    newTitle,
  }: WithServerSecret<{
    repoId: string
    teamId: string
    userId: string
    eventId: string
    isoNow: string
    newTitle: string
  }>
) {
  ensureBaseHubTransaction(tx)
  const [team, repo] = await Promise.all([
    teamMutators.getTeam(tx, teamId),
    teamSpaceMutators.getRepository(tx, repoId),
  ])
  if (!team) throw new Error(`team ${teamId} not found`)
  if (!repo) throw new Error(`repo ${repoId} not found`)
  hasAccessOrThrow({
    serverSecret: tx.serverSecret,
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  const slug = await getRepoSlugForTitle(tx, {
    title: newTitle,
    id: repoId,
  })

  await Promise.all([
    repositoryBaseOps.update(tx, {
      id: repoId,
      title: newTitle,
      slug,
    }),
    teamSpaceMutators.createEvent(tx, {
      event: {
        type: 'repo-rename',
        teamId: team.id,
        oldRepoName: repo.title,
        createdAt: isoNow,
        id: eventId,
        userId: userId,
        newRepoName: newTitle,
        repoId: repoId,
      },
    }),
  ])

  return { newSlug: slug, oldSlug: repo.slug }
}

const updateRepositoryStatus = async (
  tx: WriteTransaction,
  {
    repoId,
    status,
  }: {
    repoId: string
    status: Repository['status']
  }
) => {
  const repo = await teamSpaceMutators.getRepository(tx, repoId)
  if (!repo) throw new Error(`repo ${repoId} not found`)

  await repositoryBaseOps.update(tx, { id: repoId, status })
}

const updateRepositoryPublicInfo = async (
  tx: WriteTransaction,
  {
    repoId,
    ...rest
  }: WithServerSecret<
    {
      repoId: string
    } & Partial<
      Pick<
        Repository,
        | 'isPublic'
        | 'description'
        | 'previewUrl'
        | 'gitRepoUrl'
        | 'revalidateURLs'
      >
    >
  >
) => {
  ensureBaseHubTransaction(tx)
  const repo = await teamSpaceMutators.getRepository(tx, repoId)
  if (!repo) throw new Error(`repo ${repoId} not found`)
  hasAccessOrThrow({
    serverSecret: tx.serverSecret,
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  const changedVisibility =
    rest.isPublic !== undefined && rest.isPublic !== repo.isPublic

  await repositoryBaseOps.update(tx, { id: repoId, ...rest })

  return { changedVisibility, slug: repo.slug }
}

const updateRepositoryPrivacy = async (
  tx: WriteTransaction,
  {
    repoId,
    isPublic,
  }: WithServerSecret<{
    repoId: string
    isPublic: Repository['isPublic']
  }>
) => {
  ensureBaseHubTransaction(tx)
  const repo = await teamSpaceMutators.getRepository(tx, repoId)
  if (!repo) throw new Error(`repo ${repoId} not found`)
  hasAccessOrThrow({
    serverSecret: tx.serverSecret,
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  await repositoryBaseOps.update(tx, { id: repoId, isPublic })
}

export const updateRepositoryToken = async (
  tx: WriteTransaction,
  {
    teamId,
    repoId,
    currentToken,
    newToken,
  }: WithServerSecret<{
    serverSecret: string
    teamId: string
    repoId: string
    currentToken: string
    newToken: string
  }>
) => {
  ensureBaseHubTransaction(tx)
  serverAuthorizedOrThrow(tx.serverSecret)

  const [team, repo] = await Promise.all([
    teamBaseOps.get(tx, teamId),
    repositoryBaseOps.get(tx, repoId),
  ])
  invariant(team, 'Team not found')
  invariant(repo, 'Repo not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  await repositoryBaseOps.update(tx, {
    id: repoId,
    tokens: repo.tokens.map((t) => {
      if (t.token === currentToken) {
        return { token: newToken, scope: t.scope }
      }
      return t
    }),
  })
}

const bulkInviteEmailsToRepo = async (
  tx: WriteTransaction,
  {
    teamId,
    repoId,
    invite,
    emails,
  }: WithServerSecret<{
    teamId: string
    repoId: string
    invite: Omit<
      NonNullable<Repository['guests']>['invites'][number],
      'emailLock'
    >
    emails: NonNullable<Repository['guests']>['invites'][number]['emailLock'][]
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team for this repo not found')
  const repo = await repositoryBaseOps.get(tx, repoId)
  invariant(repo, 'Repo not found')

  // ensure user is owner
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: 'all',
    guestAccess: false,
  })

  // filter out emails that are already invited
  const newInvites: RepoInvites[] = []
  const invites: RepoInvites[] = [...(repo.guests?.invites ?? [])]
  const repoInvitesEmailsMap = new Map<string, ''>(
    invites.map((i) => [i.emailLock ?? '', ''])
  )
  emails.forEach((email) => {
    if (email && repoInvitesEmailsMap.has(email)) return
    invites.push({ ...invite, emailLock: email })
    newInvites.push({ ...invite, emailLock: email })
  })

  await repositoryBaseOps.update(tx, {
    id: repoId,
    guests: { list: repo.guests?.list ?? [], invites },
  })

  return {
    emails,
    invites: newInvites,
    ownerUsername: tx.serverOnly?.session?.sessionClaims.username ?? '',
    teamSlug: team.slug,
    repoName: repo.title,
  }
}

type InviteType<T> = T extends Array<infer U> ? U[] : T
type RepoInvites = NonNullable<Repository['guests']>['invites'][number]

const inviteToRepo = async (
  tx: WriteTransaction,
  {
    teamId,
    repoId,
    invites,
  }: WithServerSecret<{
    teamId: string
    repoId: string
    invites: InviteType<RepoInvites | RepoInvites[]>
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team for this repo not found')
  const repo = await repositoryBaseOps.get(tx, repoId)
  invariant(repo, 'Repo not found')

  // ensure user is owner
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: 'all',
    guestAccess: false,
  })

  await repositoryBaseOps.update(tx, {
    id: repoId,
    guests: {
      list: repo.guests?.list ?? [],
      invites: Array.isArray(invites)
        ? [...(repo.guests?.invites ?? []), ...invites]
        : [...(repo.guests?.invites ?? []), invites],
    },
  })

  return {
    invites,
    ownerUsername: tx.serverOnly?.session?.sessionClaims.username ?? '',
    teamSlug: team.slug,
    repoName: repo.title,
  } as {
    invites: InviteType<RepoInvites | RepoInvites[]>
    ownerUsername: string
    teamSlug: string
    repoName: string
  }
}

export const removeRepoInvitation = async (
  tx: WriteTransaction,
  {
    repoId,
    inviteToken,
    emailLock,
  }: WithServerSecret<{
    repoId: string
    inviteToken: string
    emailLock?: Invite['emailLock']
  }>
) => {
  ensureBaseHubTransaction(tx)
  const repo = await repositoryBaseOps.get(tx, repoId)
  invariant(repo, 'Repo not found')

  // ensure user is owner
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner'],
    guestAccess: false,
  })

  await repositoryBaseOps.update(tx, {
    id: repoId,
    guests: {
      list: repo.guests?.list ?? [],
      invites: (repo.guests?.invites ?? []).filter((i) => {
        if (!emailLock) return i.token !== inviteToken

        return i.emailLock !== emailLock || i.token !== inviteToken
      }),
    },
  })
}

export const resetRepoInvitation = async (
  tx: WriteTransaction,
  {
    teamId,
    repoId,
    oldInviteToken,
    newInviteToken,
    isoNow,
  }: WithServerSecret<{
    teamId: string
    repoId: string
    oldInviteToken: string
    newInviteToken: string
    isoNow: string
  }>
) => {
  ensureBaseHubTransaction(tx)

  // ensure user is owner
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner'],
    guestAccess: false,
  })

  const repo = await repositoryBaseOps.get(tx, repoId)
  invariant(repo, 'Repo not found')

  if (repo.guests) {
    const invite = repo.guests.invites.find((i) => i.token === oldInviteToken)
    invariant(!!invite, 'Invite not found')
    invariant(!invite.emailLock, 'Can not reset invite for email invite')
    invariant(!invite.userIdLock, 'Can not reset invite for user invite')
  }

  await repositoryBaseOps.update(tx, {
    id: repoId,
    guests: {
      list: repo.guests?.list ?? [],
      invites: (repo.guests?.invites ?? []).filter(
        (i) => i.token !== oldInviteToken
      ),
    },
  })

  await inviteToRepo(tx, {
    invites: [
      {
        createdAt: isoNow,
        emailLock: null,
        token: newInviteToken,
        useCount: 0,
        userIdLock: null,
      },
    ],
    repoId: repoId,
    teamId: teamId,
  })
}

export const removeRepoGuest = async (
  tx: WriteTransaction,
  {
    repoId,
    userId,
  }: WithServerSecret<{
    repoId: string
    userId: NonNullable<Repository['guests']>['list'][number]['userId']
  }>
) => {
  ensureBaseHubTransaction(tx)
  const repo = await repositoryBaseOps.get(tx, repoId)
  invariant(repo, 'Repo not found')

  // ensure user is owner
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner'],
    guestAccess: false,
  })

  await repositoryBaseOps.update(tx, {
    id: repoId,
    guests: {
      list: (repo.guests?.list ?? []).filter(
        (guest) => guest.userId !== userId
      ),
      invites: repo.guests?.invites ?? [],
    },
  })
}

const acceptInviteToRepo = async (
  tx: WriteTransaction,
  {
    teamId,
    repoId,
    inviteToken,
    isoNow,
    userId,
  }: WithServerSecret<{
    teamId: string
    repoId: string
    inviteToken: string
    isoNow: string
    userId: string
  }>
) => {
  ensureBaseHubTransaction(tx)
  const [team, repo] = await Promise.all([
    teamBaseOps.get(tx, teamId),
    repositoryBaseOps.get(tx, repoId),
  ])
  invariant(team, 'Team for this repo not found')
  invariant(repo, 'Repo not found')

  if (tx.serverOnly) {
    invariant(
      tx.serverOnly.session && userId === tx.serverOnly.session.userId,
      'Unauthorized'
    )
  }

  const invite = repo.guests?.invites.find((i) => i.token === inviteToken)
  invariant(invite, 'Invite not found')

  if (tx.serverOnly) {
    const status = checkInviteValidity({
      session: tx.serverOnly.session ?? null,
      team,
      repo,
      invite,
    })
    if (status !== 'valid') {
      throw new Error(`Invalid invite: ${status}`)
    }
  }

  // you're in!

  await repositoryBaseOps.update(tx, {
    id: repoId,
    guests: {
      invites: (repo.guests?.invites ?? []).map((i) => {
        if (i.token === invite.token) {
          return { ...invite, useCount: invite.useCount + 1 }
        } else if (
          i.emailLock &&
          i.emailLock.toLowerCase() ===
            tx.serverOnly?.session?.sessionClaims.email.toLowerCase()
        ) {
          return { ...i, useCount: i.useCount + 1 }
        }
        return i
      }),
      list: [
        ...(repo.guests?.list ?? []),
        {
          createdAt: isoNow,
          userId,
        },
      ],
    },
  })
}

export const updateRepositoryAssistant = async (
  tx: WriteTransaction,
  {
    repoId,
    newAssistantId,
    serverSecret,
  }: {
    serverSecret: string
    repoId: string
    newAssistantId: string
  }
) => {
  serverAuthorizedOrThrow(serverSecret)

  const repo = await repositoryBaseOps.get(tx, repoId)

  invariant(repo, 'Repo not found')

  await repositoryBaseOps.update(tx, {
    id: repoId,
    assistantId: newAssistantId,
  })
}

export const updateRepositoryAgentsConfig = async (
  tx: WriteTransaction,
  { repoId, config }: { repoId: string; config: Repository['agentsConfig'] }
) => {
  ensureBaseHubTransaction(tx)

  const repo = await repositoryBaseOps.get(tx, repoId)
  invariant(repo, 'Repo not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        tx.serverOnly.team &&
        isUserRole({
          team: tx.serverOnly.team,
          userId: tx.serverOnly.session.userId,
          repo,
          roles: ['owner', 'member'],
        }),
      'Unauthorized'
    )
  }

  await repositoryBaseOps.update(tx, { id: repoId, agentsConfig: config })
}

export const updateRepositoryBranchingConfig = async (
  tx: WriteTransaction,
  {
    repoId,
    config,
  }: {
    repoId: string
    config: Repository['branching']
  }
) => {
  const repo = await repositoryBaseOps.get(tx, repoId)

  invariant(repo, 'Repo not found')

  await repositoryBaseOps.update(tx, {
    id: repoId,
    branching: config,
  })
}

/**
 * Update stuff like forks / bookmarks count.
 */
const updateRepoPublicStats = async (
  tx: WriteTransaction,
  {
    id,
    updateOp,
    serverSecret,
  }: {
    id: string
    updateOp: { type: 'bookmark' | 'fork'; count: number }
    serverSecret: string
  }
) => {
  serverAuthorizedOrThrow(serverSecret)
  const repo = await repositoryBaseOps.get(tx, id)

  if (!repo) return // repo may have been deleted, we just return

  const updatedStats = { ...(repo.stats ?? { bookmarks: 0, forks: 0 }) }
  switch (updateOp.type) {
    case 'bookmark':
      updatedStats.bookmarks = updateOp.count
      break
    case 'fork':
      updatedStats.forks = updateOp.count
      break
    default:
      throw new Error(`Invalid update type: ${updateOp.type}`)
  }

  invariant(updateOp.count >= 0, "Stats can't be a negative number.")

  await repositoryBaseOps.update(tx, { id, stats: updatedStats })
}

const listRepositories = async (
  tx: ReadTransaction,
  {
    teamId,
    userId,
  }: WithServerSecret<{
    teamId: string
    userId: string
  }>
) => {
  const team = await teamSpaceMutators.getTeam(tx, teamId)
  const member = team?.members.find((m) => m.userId === userId)
  invariant(member, 'User is not a member of the team')

  const listPromise = repositoryBaseOps.list(tx)
  if (member.hasAllAccess) {
    return await listPromise
  }
  if (!member.repoIds || member.repoIds.length < 1) {
    return []
  }

  return (await listPromise).filter((r) => {
    return member.repoIds?.includes(r.id) || r.isPublic
  })
}

/* --------------------------------------------------------------------------- */
/*                              Integrations 📚                                */
/* -------------------------------------------------------------------------- */

const addIntegrationToRepo = async (
  tx: WriteTransaction,
  {
    integrationId,
    integrationType,
    projectId,
    id,
  }: WithServerSecret<{
    id: string
    integrationId: Integration['id']
    integrationType: Integration['type']
    configurationId: Integration['configurationId']
    projectId: string
  }>
) => {
  ensureBaseHubTransaction(tx)
  const repo = await repositoryBaseOps.get(tx, id)
  if (!repo) throw new Error(`repo ${id} not found`)

  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  return await repositoryBaseOps.update(tx, {
    id,
    integrations: [
      ...(repo.integrations ?? []),
      {
        id: integrationId,
        type: integrationType,
        projectId,
      },
    ],
  })
}

/* -------------------------------------------------------------------------------------------------
 * README
 * -----------------------------------------------------------------------------------------------*/

export const updateVercelDeployREADME = async (
  tx: WriteTransaction,
  {
    repoId,
    config,
  }: WithServerSecret<{
    repoSlug: string
    repoId: string
    config: VercelDeployButtonConfig
  }>
) => {
  ensureBaseHubTransaction(tx)
  const repo = await repositoryBaseOps.get(tx, repoId)
  if (!repo) throw new Error(`repo ${repoId} not found`)

  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  await repositoryBaseOps.update(tx, {
    id: repoId,
    readme: {
      ...repo.readme,
      vercelDeployButton: config,
    },
  })
}

export const updateScreenshotsREADME = async (
  tx: WriteTransaction,
  {
    repoId,
    screenshots,
  }: WithServerSecret<{
    repoSlug: string
    repoId: string
    screenshots: READMEScreenshots
  }>
) => {
  ensureBaseHubTransaction(tx)
  const repo = await repositoryBaseOps.get(tx, repoId)
  if (!repo) throw new Error(`repo ${repoId} not found`)

  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  await repositoryBaseOps.update(tx, {
    id: repoId,
    readme: { ...repo.readme, screenshots },
  })
}

export const updateAdditionalInfoREADME = async (
  tx: WriteTransaction,
  {
    repoId,
    additionalInfo,
  }: WithServerSecret<{
    repoSlug: string
    repoId: string
    additionalInfo: READMEAdditionalInfo
  }>
) => {
  ensureBaseHubTransaction(tx)
  const repo = await repositoryBaseOps.get(tx, repoId)
  if (!repo) throw new Error(`repo ${repoId} not found`)

  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  await repositoryBaseOps.update(tx, {
    id: repoId,
    readme: { ...repo.readme, additionalInfo },
  })
}

export const repositoryMutators = {
  getRepository: repositoryBaseOps.get,
  putRepository: repositoryBaseOps.put,
  updateRepositoryToken,
  deleteRepository,
  updateRepositoryPrivacy,
  updateRepositoryPublicInfo,
  listRepositories,
  updateRepositoryStatus,
  createRepository,
  getRepoSlugForTitle,
  getRepoBySlug,
  updateLatestCommit,
  addContributor,
  renameRepository,
  updateRepoPublicStats,
  updateRepositoryAssistant,
  updateRepositoryBranchingConfig,
  updateRepositoryAgentsConfig,
  /* Integration */
  addIntegrationToRepo,
  createIntegration: integrationBaseOps.put,
  /* README */
  updateVercelDeployREADME,
  updateScreenshotsREADME,
  updateAdditionalInfoREADME,
  /* Invites */
  bulkInviteEmailsToRepo,
  inviteToRepo,
  removeRepoInvitation,
  removeRepoGuest,
  acceptInviteToRepo,
  resetRepoInvitation,
}
